import React, { useCallback, useEffect, useState } from "react";
import { Card, Col, DatePicker, Row, Table, Typography } from "antd";
import dayjs from "dayjs";
import adminApi from "../../../services/adminApi";
import ReportShell from "../ReportShell";
import {
  DateRange,
  DEFAULT_RANGE,
  RANGE_PRESETS,
  rangeParams,
} from "../reportCommon";
import { formatCurrency } from "../../../utils/reportExport";

const { RangePicker } = DatePicker;
const { Text } = Typography;

const OverviewReport: React.FC = () => {
  const [range, setRange] = useState<DateRange>(DEFAULT_RANGE);
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApi.getEnterpriseReports(rangeParams(range));
      setData(res.data.data);
    } catch (e: any) {
      setError(e.response?.data?.message || "Failed to load overview");
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    load();
  }, [load]);

  const sales = data?.sales || {};
  const purchases = data?.purchases || {};
  const profit = data?.profit || {};
  const inventory = data?.inventory || {};
  const valuation = inventory.valuation || {};

  return (
    <ReportShell
      title="Overview"
      loading={loading}
      error={error}
      onRefresh={load}
      onReset={() => setRange(DEFAULT_RANGE)}
      filters={
        <RangePicker
          size="small"
          value={range}
          allowClear={false}
          presets={RANGE_PRESETS}
          onChange={(v) => v && setRange(v as DateRange)}
        />
      }
      stats={[
        {
          title: "Sales (incl. tax)",
          value: sales.totalSales || 0,
          prefix: "₹",
          precision: 2,
        },
        { title: "Bills", value: sales.billCount || 0 },
        {
          title: "Collected",
          value: sales.amountCollected || 0,
          prefix: "₹",
          precision: 2,
        },
        {
          title: "Purchases",
          value: purchases.totalPurchases || 0,
          prefix: "₹",
          precision: 2,
        },
        {
          title: "Gross profit",
          value: profit.grossProfit || 0,
          prefix: "₹",
          precision: 2,
        },
        {
          title: "Stock value (cost)",
          value: valuation.purchaseValue || 0,
          prefix: "₹",
          precision: 2,
        },
      ]}
    >
      <Row gutter={[12, 12]}>
        <Col xs={24} lg={8}>
          <Card
            title={`Near expiry (${(inventory.nearExpiry || []).length})`}
            size="small"
            loading={loading}
          >
            <Table
              rowKey="_id"
              size="small"
              dataSource={(inventory.nearExpiry || []).slice(0, 8)}
              pagination={false}
              columns={[
                { title: "Product", dataIndex: "name", ellipsis: true },
                {
                  title: "Expiry",
                  dataIndex: "expiryDate",
                  width: 100,
                  render: (d: string) =>
                    d ? (
                      <Text type="warning">{dayjs(d).format("DD/MM/YY")}</Text>
                    ) : (
                      "—"
                    ),
                },
              ]}
            />
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card
            title={`Expired (${(inventory.expired || []).length})`}
            size="small"
            loading={loading}
          >
            <Table
              rowKey="_id"
              size="small"
              dataSource={(inventory.expired || []).slice(0, 8)}
              pagination={false}
              columns={[
                { title: "Product", dataIndex: "name", ellipsis: true },
                {
                  title: "Expired",
                  dataIndex: "expiryDate",
                  width: 100,
                  render: (d: string) =>
                    d ? <Text type="danger">{dayjs(d).format("DD/MM/YY")}</Text> : "—",
                },
              ]}
            />
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card
            title={`Low stock (${(inventory.lowStock || []).length})`}
            size="small"
            loading={loading}
          >
            <Table
              rowKey="_id"
              size="small"
              dataSource={(inventory.lowStock || []).slice(0, 8)}
              pagination={false}
              columns={[
                { title: "Product", dataIndex: "name", ellipsis: true },
                {
                  title: "Qty",
                  width: 70,
                  align: "right" as const,
                  render: (_: unknown, r: any) => (
                    <Text type="danger">{r.quantity?.current ?? 0}</Text>
                  ),
                },
              ]}
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Sales vs purchases" size="small" loading={loading}>
            <Table
              size="small"
              rowKey="label"
              pagination={false}
              showHeader={false}
              dataSource={[
                { label: "Sales (incl. tax)", value: sales.totalSales },
                { label: "Taxable sales", value: sales.taxableSales },
                { label: "Sales tax", value: sales.totalTax },
                { label: "Purchases", value: purchases.totalPurchases },
                { label: "Purchase payables", value: purchases.payables },
              ]}
              columns={[
                { dataIndex: "label" },
                {
                  dataIndex: "value",
                  align: "right" as const,
                  render: (v: number) => formatCurrency(v),
                },
              ]}
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Profit summary" size="small" loading={loading}>
            <Table
              size="small"
              rowKey="label"
              pagination={false}
              showHeader={false}
              dataSource={[
                { label: "Taxable sales", value: profit.salesValue },
                { label: "Cost (incl. GST)", value: profit.costValue },
                { label: "Gross profit", value: profit.grossProfit, strong: true },
              ]}
              columns={[
                {
                  dataIndex: "label",
                  render: (v: string, r: any) =>
                    r.strong ? <strong>{v}</strong> : v,
                },
                {
                  dataIndex: "value",
                  align: "right" as const,
                  render: (v: number, r: any) =>
                    r.strong ? (
                      <strong>{formatCurrency(v)}</strong>
                    ) : (
                      formatCurrency(v)
                    ),
                },
              ]}
            />
          </Card>
        </Col>
      </Row>
    </ReportShell>
  );
};

export default OverviewReport;
